import { lazy } from "react";

/**
 * Componentes de página carregados sob demanda (lazy) organizados por domínio
 *
 * Este arquivo centraliza todos os imports lazy para manter o arquivo principal do router limpo.
 */

// ============================================================================
// Páginas Públicas
// ============================================================================

export const LoginPage = lazy(() =>
  import("@/pages/auth/login").then((module) => ({
    default: module.LoginPage,
  })),
);

export const PublicTalentUpdatePage = lazy(() =>
  import("@/pages/talent/request-update").then((module) => ({
    default: module.PublicTalentUpdatePage,
  })),
);

export const PublicScheduleRegistrationPage = lazy(() =>
  import("@/pages/schedule/registration-update").then((module) => ({
    default: module.PublicScheduleRegistrationPage,
  })),
);

export const PublicContractPreviewPage = lazy(() =>
  import("@/pages/contracts/preview").then((module) => ({
    default: module.PublicContractPreviewPage,
  })),
);

// ============================================================================
// Páginas Protegidas - Compartilhadas
// ============================================================================

export const TalentsPage = lazy(() =>
  import("@/pages/talent").then((module) => ({
    default: module.TalentsPage,
  })),
);

export const TalentDetailsPage = lazy(() =>
  import("@/pages/talent/[id]/details").then((module) => ({
    default: module.TalentDetailsPage,
  })),
);

export const TalentDataPage = lazy(() =>
  import("@/pages/talent/[id]/data").then((module) => ({
    default: module.TalentDataPage,
  })),
);

export const TalentDataEditPage = lazy(() =>
  import("@/pages/talent/[id]/data/edit").then((module) => ({
    default: module.TalentDataEditPage,
  })),
);

export const TalentBookPage = lazy(() =>
  import("@/pages/talent/[id]/book").then((module) => ({
    default: module.TalentBookPage,
  })),
);

export const TalentDocumentsPage = lazy(() =>
  import("@/pages/talent/[id]/documents").then((module) => ({
    default: module.TalentDocumentsPage,
  })),
);

export const TalentContractsPage = lazy(() =>
  import("@/pages/talent/[id]/contracts").then((module) => ({
    default: module.TalentContractsPage,
  })),
);

export const TalentCreatePage = lazy(() =>
  import("@/pages/talent/create").then((module) => ({
    default: module.TalentCreatePage,
  })),
);

export const ArchivedTalentsPage = lazy(() =>
  import("@/pages/talent/archived").then((module) => ({
    default: module.ArchivedTalentsPage,
  })),
);

export const JobsPage = lazy(() =>
  import("@/pages/job").then((module) => ({
    default: module.JobsPage,
  })),
);

export const NewJobPage = lazy(() =>
  import("@/pages/job/newJob").then((module) => ({
    default: module.NewJobPage,
  })),
);

export const EditJobPage = lazy(() =>
  import("@/pages/job/castingStage/editStageDetails").then((module) => ({
    default: module.EditJobPage,
  })),
);

export const JobDetailsPage = lazy(() =>
  import("@/pages/job/castingStage/stageDetails").then((module) => ({
    default: module.JobDetailsPage,
  })),
);

export const JobCastingTalentsListPage = lazy(() =>
  import("@/pages/job/castingStage/talentsList").then((module) => ({
    default: module.JobCastingTalentsListPage,
  })),
);

export const PreSelectionStagePage = lazy(() =>
  import("@/pages/job/preSelectionStage").then((module) => ({
    default: module.PreSelectionStagePage,
  })),
);

export const EditedStagePage = lazy(() =>
  import("@/pages/job/editedStage").then((module) => ({
    default: module.EditedStagePage,
  })),
);

export const ApprovedStagePage = lazy(() =>
  import("@/pages/job/approvedStage").then((module) => ({
    default: module.ApprovedStagePage,
  })),
);

export const CollaboratorsPage = lazy(() =>
  import("@/pages/collaborator").then((module) => ({
    default: module.CollaboratorsPage,
  })),
);

export const MyAccountPage = lazy(() =>
  import("@/pages/account").then((module) => ({
    default: module.MyAccountPage,
  })),
);

// ============================================================================
// Páginas Exclusivas de Casting
// ============================================================================

export const CompositesPage = lazy(() =>
  import("@/pages/casting/composites").then((module) => ({
    default: module.CompositesPage,
  })),
);

export const CreateCompositePage = lazy(() =>
  import("@/pages/casting/composites/create").then((module) => ({
    default: module.CreateCompositePage,
  })),
);

export const EditCompositePage = lazy(() =>
  import("@/pages/casting/composites/edit").then((module) => ({
    default: module.EditCompositePage,
  })),
);

export const TalentJobsPage = lazy(() =>
  import("@/pages/casting/talent-jobs").then((module) => ({
    default: module.TalentJobsPage,
  })),
);

// ============================================================================
// Páginas Customizadas do Laboratório
// ============================================================================

export const LaboratorioTalentsPage = lazy(() =>
  import("@/pages/photoLab/talents").then((module) => ({
    default: module.LaboratorioTalentsPage,
  })),
);

export const LaboratorioTalentBookPage = lazy(() =>
  import("@/pages/photoLab/talent-book").then((module) => ({
    default: module.LaboratorioTalentBookPage,
  })),
);

export const LaboratorioCollaboratorsPage = lazy(() =>
  import("@/pages/collaborator").then((module) => ({
    default: module.CollaboratorsPage,
  })),
);

// ============================================================================
// Páginas de Produção
// ============================================================================

export const SchedulePage = lazy(() =>
  import("@/pages/schedule").then((module) => ({
    default: module.SchedulePage,
  })),
);

export const ScheduleTalentDataPage = lazy(() =>
  import("@/pages/schedule/[scheduleId]/data").then((module) => ({
    default: module.ScheduleTalentDataPage,
  })),
);

export const ScheduleProductionPage = lazy(() =>
  import("@/pages/schedule/[scheduleId]/production").then((module) => ({
    default: module.ScheduleProductionPage,
  })),
);

export const ScheduleContractsPage = lazy(() =>
  import("@/pages/schedule/[scheduleId]/contracts").then((module) => ({
    default: module.ScheduleContractsPage,
  })),
);

export const ScheduleArtisticProductionPage = lazy(() =>
  import("@/pages/schedule/[scheduleId]/artistic-production").then((module) => ({
    default: module.ScheduleArtisticProductionPage,
  })),
);

export const SchedulePhotographyPage = lazy(() =>
  import("@/pages/schedule/[scheduleId]/photography").then((module) => ({
    default: module.SchedulePhotographyPage,
  })),
);

export const RejectedTalentDataPage = lazy(() =>
  import("@/pages/production/rejected/[talentId]/data").then((module) => ({
    default: module.RejectedTalentDataPage,
  })),
);

export const ProductionRejectedPage = lazy(() =>
  import("@/pages/production/rejected").then((module) => ({
    default: module.ProductionRejectedPage,
  })),
);

// ============================================================================
// Componentes de Layout
// ============================================================================

export const RootLayout = lazy(() =>
  import("@/layouts/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);

export const ProtectedLayout = lazy(() =>
  import("@/layouts/ProtectedLayout").then((module) => ({
    default: module.ProtectedLayout,
  })),
);

export const PublicLayout = lazy(() =>
  import("@/layouts/PublicLayout").then((module) => ({
    default: module.PublicLayout,
  })),
);

export const TalentCastingBasePage = lazy(() =>
  import("@/pages/casting/talent/TalentCastingBasePage").then((module) => ({
    default: module.TalentCastingBasePage,
  })),
);

export const TalentPhotoLabBasePage = lazy(() =>
  import("@/pages/photoLab/talent/TalentPhotoLabBasePage").then((module) => ({
    default: module.TalentPhotoLabBasePage,
  })),
);

export const StageLayout = lazy(() =>
  import("@/layouts/StageLayout").then((module) => ({
    default: module.StageLayout,
  })),
);

// ============================================================================
// Páginas de Desenvolvimento e Teste
// ============================================================================

export const TableTestPage = lazy(() =>
  import("@/pages/dev/table-test").then((module) => ({
    default: module.TableTestPage,
  })),
);
